import React from "react"
import { getSiblingsLotteryResultReferenceLabel, getYesNoLabel } from '../../../../containers/common/codeMaster'
import { ChildLotteryReservationInfoType } from '../../../../containers/common/lotteryReservation'
import { FieldsType, FieldRule, getByChildFormFieldRule } from '../../../../containers/common/projectMst'
import { GetProjectDetailDto } from '../../../../dataAccess/webApi/dto/projectsDto'
import { LabelGItem, ConvertFalsyValueToUnfilledValueGItem, ValueGItem } from './lotteryReservationDetailParts'
import { translate } from '../../../../i18n'
import { yesNo } from "../../../../containers/common/constant/classification"
import { Stack } from "@mui/material"
import { formatYmdWeek } from "../../../../utils/dateUtil"

type ReservationDetailFieldType = {
    component: React.FC<{ childInfo: ChildLotteryReservationInfoType }>
}
type SortedReservationDetailFieldType = ReservationDetailFieldType & {
    rule: FieldRule
}

export type ByChildDetailContentProps = {
    childInfo: ChildLotteryReservationInfoType
    projectMst: GetProjectDetailDto
    showSiblingsLotteryResultReferenceFlag: boolean
}

const ByChildDetailContent: React.FC<ByChildDetailContentProps> = (props: ByChildDetailContentProps) => {
    // eslint-disable-next-line react/prop-types
    const { childInfo, projectMst, showSiblingsLotteryResultReferenceFlag } = props;

    const reservationDetail: FieldsType<ReservationDetailFieldType> = {
        diagonosis: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 病名１ */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.diagonosis')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.diagonosis?.first.stringify} />
                    {/* 病名２ */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.diagonosis2')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.diagonosis?.second.stringify} />
                </>
            )
        },
        symptom: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 症状 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.symptoms')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.symptom?.stringify} />
                </>
            )
        },
        epidemic: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 流行り病 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.epidemic')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo?.epidemic?.stringify} />
                </>
            )
        },
        medicine: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 処方薬 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.medicine.rx')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem
                        value={
                            isYesNo(childInfo.medicine?.rx)
                                ? getYesNoLabel(childInfo.medicine?.rx)
                                : undefined
                        }
                    />
                    {/* 市販薬 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.medicine.otc')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem
                        value={
                            isYesNo(childInfo.medicine?.otc)
                                ? getYesNoLabel(childInfo.medicine?.otc)
                                : undefined
                        }
                    />
                </>
            )
        },
        bodyTemp: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 体温 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.bodyTemperature')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem
                        value={childInfo.temperature?.stringify}
                        suffix={'℃'}
                    />
                </>
            )
        },
        febrileSeizures: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 熱性けいれん */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.febrileSeizures')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem
                        value={
                            isYesNo(childInfo.febrileSeizures)
                                ? getYesNoLabel(childInfo.febrileSeizures)
                                : undefined
                        }
                    />
                </>
            )
        },
        medicalDoc: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 医師連絡票提出状況 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.medicalDoc')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem
                        value={
                            isYesNo(childInfo.medicalDoc?.hasSubmitted)
                                ? getYesNoLabel(childInfo.medicalDoc?.hasSubmitted)
                                : undefined
                        }
                    />
                    { childInfo.hospitalInfo?.date && (
                        <>
                        {/* 病院名 */}
                        <LabelGItem>{translate("system.reservationDetailGItems.label.hospital.name")}</LabelGItem>
                        <ConvertFalsyValueToUnfilledValueGItem
                            value={childInfo.hospitalInfo?.hospitalName}
                        />
                        {/* 来院日時 */}
                        <LabelGItem>{translate("system.reservationDetailGItems.label.hospital.datetime")}</LabelGItem>
                        <ValueGItem>
                            <Stack>
                                <div>{formatYmdWeek(new Date(childInfo.hospitalInfo?.date))}</div>
                                <div>{childInfo.hospitalInfo?.time}</div>
                            </Stack>
                        </ValueGItem>
                        </>
                    )}
                </>
            )
        },
        emergency: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 容態変化時の対応 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.emergency')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.emergencyAction?.action.stringify} />
                </>
            )
        },
        height: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 身長 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.height')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem
                        value={childInfo.height?.stringify}
                        suffix={'cm'}
                    />
                </>
            )
        },
        weight: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 体重 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.weight')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem
                        value={childInfo.weight?.stringify}
                        suffix={'kg'}
                    />
                </>
            )
        },
        eatingMethod: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 栄養方法 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.eatingMethod')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.eatingMethod?.method.stringify} />
                </>
            )
        },
        foodAllergy: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 食物アレルギー */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.foodAllergy')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem
                        value={
                            isYesNo(childInfo.allergy?.foodFlag)
                                ? getYesNoLabel(childInfo.allergy?.foodFlag)
                                : undefined
                        }
                    />
                </>
            )
        },
        eatingAbility: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 食事状況 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.eatingAbility')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.eatingAbility?.stringify} />
                </>
            )
        },
        action: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 行動 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.action')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.actions?.action.stringify} />
                </>
            )
        },
        toiletAbility: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 排便 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.toiletAbility')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.toiletAbility?.stringify} />
                </>
            )
        },
        considerations: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 配慮が必要な事項 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.considerations')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.considerations} />
                </>
            )
        },
        reduction: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 減免の種類 */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.reduction')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo.reductionType?.reduction.stringify} />
                </>
            )
        },
        attachedDoc: {
            component: ({ childInfo }: { childInfo: ChildLotteryReservationInfoType }) => (
                <>
                    {/* 添付ファイル */}
                    <LabelGItem>{translate('system.reservationDetailGItems.label.attachedDoc')}</LabelGItem>
                    <ConvertFalsyValueToUnfilledValueGItem value={childInfo?.attachedDoc?.status} />
                </>
            )
        },
    }
    const fieldRule = getByChildFormFieldRule(projectMst)
    const sortedDetail: SortedReservationDetailFieldType[] = Object.keys(fieldRule)
        .filter((fieldKey) => fieldRule[fieldKey as keyof FieldsType<FieldRule>].show)
        .map((fieldKey) => {
            const field = fieldKey as keyof FieldsType<FieldRule>;
                return {
                    component: reservationDetail[field].component,
                    rule: fieldRule[field]
                }
        })
        .sort((a, b) => (a.rule.order ?? Number.MAX_SAFE_INTEGER) - (b.rule.order ?? Number.MAX_SAFE_INTEGER));
    
        if (childInfo.facilityInputFields) {
            Object.entries(childInfo.facilityInputFields)
            .sort(([_keyA, valueA], [_keyB, valueB]) => {
                return (valueA.order || 0) - (valueB.order || 0);
              })
            .forEach(([_key, value], index) => {
                const isAnswered = value.answer !== '' && value
                    ? translate('system.reservationDetailGItems.label.answered')
                    : translate('system.reservationDetailGItems.label.unanswered');
                sortedDetail.push({
                    component: () => (
                        <>
                            {/* 施設別予約字項目回答 */}
                            <LabelGItem>{`${translate('system.reservationDetailGItems.label.facilityInputField')}${index + 1}`}</LabelGItem>
                            <ConvertFalsyValueToUnfilledValueGItem value={isAnswered} />
                        </>
                    ),
                    rule: {
                        order: 100,
                        show: true,
                        required: true
                    },
                });
            })
        }    
        
    return (
        <>
            {sortedDetail.map(({ component: Component }, index) => (
                <Component key={index} childInfo={childInfo} />
            ))}
            {
                showSiblingsLotteryResultReferenceFlag && childInfo.childInfo?.brothersSistersFlag === yesNo.yes && (
                    <>
                        {/* きょうだい全員当選とならない場合 */}
                        <LabelGItem>{translate('system.reservationDetailGItems.label.siblingsLotteryResultReference')}</LabelGItem>
                        <ConvertFalsyValueToUnfilledValueGItem
                            value={getSiblingsLotteryResultReferenceLabel(childInfo?.siblingsLotteryResultReferenceFlag === yesNo.yes)}
                        />
                    </>
                )
            }
        </>
    );
}

const isYesNo = (value?: unknown): value is yesNo => {
    return value === yesNo.yes || value === yesNo.no;
}

export default ByChildDetailContent;
